
// ADC-IMPLEMENTS: <adc-006a-impl-overview-01>
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/lib/analytics";
import Navbar from "@/components/Navbar";
import { SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "@/components/AppSidebar";
import { ConversationList, type Conversation } from "@/components/ConversationList";
import { ConversationView } from "@/components/ConversationView";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Flag, MessageCircle, User, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnalyticsEvents, AnalyticsParams } from "@/lib/analytics";
import { useMediaQuery } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
// ADC-006A v2.0.0: Incident management moved to IncidentDashboard (/incidents).
// IncidentBanner and IncidentList are DEPRECATED and removed.
// Per-conversation useIncidents hook retained for backward compatibility.

// Enhanced function to get document counts with optional timestamp filtering
const getDocumentCount = async (
  collectionPath: string, 
  whereConditions?: Array<[string, any, any]>,
  includeTimestampFilter: boolean = true
): Promise<number> => {
  try {
    const collectionRef = collection(db, collectionPath);
    let countQuery = query(collectionRef);
    
    if (whereConditions && whereConditions.length > 0) {
      // Apply conditions based on the includeTimestampFilter flag
      const filteredConditions = includeTimestampFilter 
        ? whereConditions 
        : whereConditions.filter(condition => condition[0] !== "messageTimestamp");
      
      if (filteredConditions.length > 0) {
        // Apply the first condition
        countQuery = query(collectionRef, where(filteredConditions[0][0], filteredConditions[0][1], filteredConditions[0][2]));
        
        // Apply any additional conditions
        for (let i = 1; i < filteredConditions.length; i++) {
          countQuery = query(countQuery, where(filteredConditions[i][0], filteredConditions[i][1], filteredConditions[i][2]));
        }
      }
    }
    
    const countSnapshot = await getCountFromServer(countQuery);
    return countSnapshot.data().count;
  } catch (err) {
    console.error(`Error getting count for ${collectionPath}:`, err);
    return 0;
  }
};

export default function FlaggedMessages() {
  const { connectionId, conversationId } = useParams<{ connectionId: string; conversationId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionName, setConnectionName] = useState<string>("");
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [mobileView, setMobileView] = useState<"list" | "conversation">("list");
  // Add a ref to determine if this is a new selection or just a re-render
  const previousConversationIdRef = useRef<string | null>(null);

  // ADC-006A v2.0.0: Incident management moved to IncidentDashboard.
  // IncidentBanner, IncidentList, and per-conversation incident state removed.

  useEffect(() => {
    if (!user || !connectionId) {
      setError("Missing connection ID or not logged in");
      setLoading(false);
      return;
    }

    const loadConnectionData = async () => {
      try {
        const connectionDocRef = doc(db, `connections/${connectionId}`);
        const connectionSnap = await getDoc(connectionDocRef);
        
        if (connectionSnap.exists()) {
          const connectionData = connectionSnap.data();
          setConnectionName(connectionData.name || "Unknown Connection");
        } else {
          console.warn(`Connection document ${connectionId} not found`);
          setConnectionName("Unknown Connection");
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching connection:", err);
        setError("Failed to load connection details");
        setLoading(false);
      }
    };

    loadConnectionData();
  }, [connectionId, user]);

  useEffect(() => {
    if (conversationId && connectionId) {
      // Check if this is a new selection or just a re-render
      const isNewSelection = previousConversationIdRef.current !== conversationId;
      previousConversationIdRef.current = conversationId;
      
      // Only reload conversation data if it's a new selection or we don't have data yet
      if (isNewSelection || (!selectedConversation && conversationId)) {
        const loadConversation = async () => {
          try {
            const conversationDocRef = doc(db, `connections/${connectionId}/conversations/${conversationId}`);
            const conversationSnap = await getDoc(conversationDocRef);
            
            if (conversationSnap.exists()) {
              const data = conversationSnap.data();
              
              const messagesPath = `connections/${connectionId}/conversations/${conversationId}/messages`;
              
              // Set up query conditions
              const queryConditions: Array<[string, any, any]> = [
                ["isFlaggedMessage", "==", true]
              ];
              
              const lastFlaggedReviewAt = data.lastFlaggedReviewAt 
                ? data.lastFlaggedReviewAt.toDate() 
                : null;
                
              if (lastFlaggedReviewAt) {
                queryConditions.push([
                  "messageTimestamp", 
                  ">", 
                  Math.floor(lastFlaggedReviewAt.getTime() / 1000)
                ]);
              }
              
              // Get count of unreviewed flagged messages
              const flaggedCount = await getDocumentCount(messagesPath, queryConditions, true);
              
              // Get total count of all flagged messages regardless of review time
              const totalFlaggedCount = await getDocumentCount(messagesPath, queryConditions, false);
              
              console.log(`[FlaggedMessages] Conversation ${conversationId} has ${flaggedCount} unreviewed flagged messages and ${totalFlaggedCount} total flagged messages`);
              
              setSelectedConversation({
                id: conversationId,
                jid: data.jid || conversationId,
                type: data.type || 'dm',
                name: data.name || "Unknown",
                lastMessageTimestamp: data.lastMessageTimestamp || 0,
                lastMessagePreview: data.lastMessagePreview || "",
                flaggedCount: flaggedCount,
                totalFlaggedCount: totalFlaggedCount,
                photoUrl: data.photoUrl,
                participants: data.participants || [],
                lastFlaggedReviewAt: data.lastFlaggedReviewAt ? data.lastFlaggedReviewAt.toDate() : null
              });
              
              if (isMobile) {
                setMobileView("conversation");
              }
            } else {
              console.warn(`Conversation ${conversationId} not found`);
              setError(`Conversation not found`);
            }
          } catch (err) {
            console.error("Error fetching conversation:", err);
            setError("Failed to load conversation details");
          }
        };
        
        loadConversation();
      }
    } else if (!conversationId) {
      // Clear selected conversation when no conversationId is provided
      setSelectedConversation(null);
      previousConversationIdRef.current = null;
    }
  }, [conversationId, connectionId, isMobile]);

  useEffect(() => {
    if (isMobile && selectedConversation) {
      setMobileView("conversation");
    }
  }, [selectedConversation, isMobile]);

  const handleSelectConversation = (conversation: Conversation) => {
    trackEvent(AnalyticsEvents.FEATURE_USED, {
      [AnalyticsParams.FEATURE_NAME]: 'select_conversation',
      [AnalyticsParams.CONVERSATION_TYPE]: conversation.type
    });
    
    // If selecting the same conversation, don't trigger a re-render
    if (selectedConversation?.id === conversation.id) {
      return;
    }
    
    setSelectedConversation(conversation);
    navigate(`/${connectionId}/conversations/${conversation.id}`);
  };

  const handleToggleFlaggedOnly = () => {
    trackEvent(AnalyticsEvents.FEATURE_USED, {
      [AnalyticsParams.FEATURE_NAME]: showFlaggedOnly ? 'show_all_messages' : 'show_flagged_only',
    });
    
    setShowFlaggedOnly(!showFlaggedOnly);
  };

  const handleBackToConversations = () => {
    if (isMobile) {
      setMobileView("list");
    } else {
      setSelectedConversation(null);
      previousConversationIdRef.current = null;
    }
    
    navigate(`/${connectionId}/conversations`);
  };
  
  const handleBackNavigation = () => {
    if (conversationId) {
      handleBackToConversations();
    } else {
      navigate("/connections");
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="flex gap-4">
            <Skeleton className="h-[calc(100vh-8rem)] w-80" />
            <Skeleton className="h-[calc(100vh-8rem)] flex-1" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (isMobile) {
      if (mobileView === "list" || !selectedConversation) {
        return (
          <div className="space-y-4">
            <ConversationList
              connectionId={connectionId || ""}
              onSelectConversation={handleSelectConversation}
              selectedConversationId={selectedConversation?.id}
            />
          </div>
        );
      } else {
        return (
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <Avatar className="h-9 w-9 mr-3">
                {selectedConversation.photoUrl ? (
                  <AvatarImage src={selectedConversation.photoUrl} alt={selectedConversation.name} />
                ) : (
                  <AvatarFallback className={selectedConversation.type === 'group' ? 'bg-primary/10' : 'bg-muted'}>
                    {selectedConversation.type === 'group' ? (
                      <Users className="h-4 w-4 text-primary" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <div className="font-medium">{selectedConversation.name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  <span>
                    {selectedConversation.flaggedCount > 0
                      ? `${selectedConversation.flaggedCount}/${selectedConversation.totalFlaggedCount || selectedConversation.flaggedCount} flagged messages`
                      : `${selectedConversation.totalFlaggedCount || 0} flagged messages`}
                  </span>
                </div>
              </div>
            </div>

            <ConversationView
              connectionId={connectionId || ""}
              conversation={selectedConversation}
              showFlaggedOnly={showFlaggedOnly}
              onToggleFlaggedOnly={handleToggleFlaggedOnly}
            />
          </div>
        );
      }
    }

    return (
      <div className="flex gap-4">
        <div className="w-80 shrink-0">
          <ConversationList
            connectionId={connectionId || ""}
            onSelectConversation={handleSelectConversation}
            selectedConversationId={selectedConversation?.id}
            className="sticky top-4"
          />
        </div>

        <div className="flex-1">
          {selectedConversation ? (
            <>
              <ConversationView
                connectionId={connectionId || ""}
                conversation={selectedConversation}
                showFlaggedOnly={showFlaggedOnly}
                onToggleFlaggedOnly={handleToggleFlaggedOnly}
              />
            </>
          ) : (
            <Card>
              <CardContent className="pt-6 flex flex-col items-center justify-center text-center p-6">
                <div className="rounded-full bg-primary/10 p-3 mb-4">
                  <Flag className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                <p className="text-muted-foreground">
                  Choose a conversation from the list to view flagged messages
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background w-full">
        <Navbar />
        
        <div className="flex min-h-[calc(100vh-4rem)] pt-16 w-full">
          <AppSidebar />
          
          <main className="flex-1 overflow-auto px-4 md:px-6 pb-6 pt-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center mb-6">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="mr-2"
                  onClick={handleBackNavigation}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold">
                    Conversations
                  </h1>
                  {connectionName && (
                    <p className="text-muted-foreground mt-1">
                      {connectionName}
                    </p>
                  )}
                </div>
              </div>
              
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
